// Messaging-inbox hooks — the WRAPPER layer (lint-exempt for TanStack
// Query). Threads/messages/badge are PERSON-scoped (the `/api/v1/me/**` surface, no tenant/band in the path):
// the keys live under ['me','threads'|'thread'|'inbox',…] so they SURVIVE a band switch (which scorches
// ['band', …]). The frozen `thread-updated` + `badge` signals (ac.ui.user.{id}) invalidate the SAME
// keys via the invalidation registry, so the live inbox + the topbar/tab-bar/app-icon badge share one cache.
//
// Optimistic rules: SEND appends optimistically (snapshot → rollback+toast onError → invalidate
// onSettled) and REACTIONS add/remove optimistically (the spec marks reactions optimistic). Read/delete/
// create-thread are server-confirm THEN repaint. Every mutation surfaces the server error verbatim and NEVER
// reports success on an unpersisted write.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError, errorMessage } from "./apiError.js";
import { useMe } from "./useSession.js";
import { pushToast } from "../toasts.js";
import { addReaction, removeReaction } from "./inboxFormat.js";
import { API_URL } from "../../config/env.js";

export type ThreadKind = "direct" | "show";
export type MessageableRead = components["schemas"]["MessageableRead"];
export type MessageablePerson = components["schemas"]["MessageablePerson"];
export type ThreadSummary = components["schemas"]["ThreadSummary"];
export type ThreadListResponse = components["schemas"]["ThreadListResponse"];
export type ThreadDetail = components["schemas"]["ThreadDetailResponse"];
export type MessageDto = components["schemas"]["MessageDto"];
export type ReactionDto = components["schemas"]["ReactionDto"];
export type ThreadParticipantDto = components["schemas"]["ThreadParticipantDto"];
export type CreateThreadRequest = components["schemas"]["CreateThreadRequest"];

// ---- query keys (person-scoped, survive a band switch — like notifications) ---------------------------

/** The thread-list key. `kind` folds into the key so each tab caches independently (`'all'` = the Messages tab). */
export const threadsKey = (kind?: ThreadKind) => ["me", "threads", kind ?? "all"] as const;
/** An open thread's detail key. The registry invalidates the parent ['me','thread'] on `thread-updated`. */
export const threadKey = (threadId: string) => ["me", "thread", threadId] as const;
/** The O(1) inbox unread-badge key — the ONE source the topbar/tab-bar/app-icon all read.*/
export const inboxBadgeKey = ["me", "inbox", "badge"] as const;
/** Who the caller may DM — the picker read, which is the create gate read forwards. */
export const messageableKey = ["me", "messageable"] as const;

// ---- queries ------------------------------------------------------------------------------------------

/** `GET /me/threads?kind` — the caller's threads, newest-first, optionally filtered by kind.*/
/**
 * `GET /me/messageable` — the people this person may start a DM with, with WHY and the band that makes it
 * true. Person-scoped: it needs no band mounted, which is what lets the inbox (a `/me` surface with no
 * tenant) offer it at all.
 *
 * **The same predicate the create gate enforces** (`core.messageable_people`), so every row here is a row
 * `POST /me/threads` will accept. Never build a picker from a seat list beside it — that is the ART-122
 * defect, where the offered set and the accepted set disagreed in both directions.
 */
export function useMessageable(enabled = true) {
  const { api } = useApp();
  return useQuery({
    queryKey: messageableKey,
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/messageable", {});
      if (!response.ok) throw new ApiError(response.status, error);
      return data as MessageableRead;
    },
  });
}

export function useThreads(kind?: ThreadKind) {
  const { api } = useApp();
  return useQuery({
    queryKey: threadsKey(kind),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/threads", {
        params: { query: kind ? { kind } : {} },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 20_000,
  });
}

/** `GET /me/threads/{threadId}` — one thread's participants + message page. Disabled with no id.*/
export function useThread(threadId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: threadKey(threadId ?? "none"),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/threads/{threadId}", {
        params: { path: { threadId: threadId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    enabled: threadId != null && threadId !== "",
    staleTime: 10_000,
  });
}

// The badge's first fetch deliberately sits out the boot rush (CD: "its first fetch is delayed 3.5s
// to stay out of the boot rush; overlay-close refreshes stay instant"). Module-level latch: only the
// FIRST mount in a session waits — every later mount (band switch remount, second shell) is instant,
// and once fetched, realtime invalidations refetch immediately as usual.
// (Pre-tripped under vitest: the delay is a production boot-rush concern, not part of any behavior
// under test, and it would stall every findBy* on the badge past its timeout.)
let inboxBadgeBootDone = import.meta.env.MODE === "test";
const INBOX_BADGE_BOOT_DELAY_MS = 3_500;

/** `GET /me/inbox/badge` — the O(1) unread count for the topbar/tab-bar/app-icon badge (single source). */
export function useInboxBadge() {
  const { api } = useApp();
  const [bootReady, setBootReady] = useState(inboxBadgeBootDone);
  useEffect(() => {
    if (inboxBadgeBootDone) return;
    const t = setTimeout(() => {
      inboxBadgeBootDone = true;
      setBootReady(true);
    }, INBOX_BADGE_BOOT_DELAY_MS);
    return () => clearTimeout(t);
  }, []);
  return useQuery({
    queryKey: inboxBadgeKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/inbox/badge");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data.unread ?? 0;
    },
    enabled: bootReady,
    staleTime: 30_000,
  });
}

// ---- mutations ----------------------------------------------------------------------------------------

interface ThreadSnapshot {
  previous?: ThreadDetail;
}

/** Roll the thread cache back to its pre-mutation snapshot (or drop it so the observer refetches truth). */
function rollbackThread(qc: QueryClient, key: readonly unknown[], previous: ThreadDetail | undefined): void {
  if (previous) qc.setQueryData(key, previous);
  else void qc.removeQueries({ queryKey: key });
}

/** Invalidate everything a new/changed message touches: the open thread, every list tab, and the badge. */
function invalidateInbox(qc: QueryClient, threadKeyRef: readonly unknown[]): void {
  void qc.invalidateQueries({ queryKey: threadKeyRef });
  void qc.invalidateQueries({ queryKey: ["me", "threads"] });
  void qc.invalidateQueries({ queryKey: inboxBadgeKey });
}

/**
 * `PATCH /me/threads/{threadId}` — rename a group chat / change its picture (ART-159). Server-confirm, not
 * optimistic: a rename is a deliberate act behind a Save, so the half-second of truth is worth more than a
 * name that appears and then reverts. Invalidates the thread AND every list, because the row people see in
 * their sidebar is the thing that just changed.
 */
export function useUpdateThread(threadId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title?: string; avatarMediaId?: string | null; clearAvatar?: boolean }) => {
      const { data, error, response } = await api.PATCH("/api/v1/me/threads/{threadId}", {
        params: { path: { threadId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: threadKey(threadId) });
      void qc.invalidateQueries({ queryKey: ["me", "threads"] });
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't save the chat")),
  });
}

/**
 * Mint a GROUP chat (ART-159) — a required name and hand-picked members.
 *
 * **No optimistic anything.** Creation is one round trip behind an explicit Create, and the row it returns is
 * what the caller routes to; an optimistic thread would be a row with a made-up id that the route then could
 * not open. The invalidation is the whole of the local update, and the other members learn about it from the
 * gate's own fan rather than from anything here.
 */
export function useCreateGroupThread() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    // Title is OPTIONAL (PO, 2026-09-13): a group is created without a name and named for its members until
    // someone renames it, so "+ New" sends no title at all.
    mutationFn: async (body: { title?: string; memberPersonIds: string[] }) => {
      const { data, error, response } = await api.POST("/api/v1/me/threads/group", { body });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["me", "threads"] }),
    onError: (err) => pushToast(errorMessage(err, "Couldn't start the chat")),
  });
}

/** Add people to a group chat. The server checks every id against the CALLER's reach, so a refusal here is
 *  a real answer and not a race — surfaced rather than swallowed. */
export function useAddThreadMembers(threadId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { personIds: string[] }) => {
      const { data, error, response } = await api.POST("/api/v1/me/threads/{threadId}/members", {
        params: { path: { threadId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: threadKey(threadId) });
      void qc.invalidateQueries({ queryKey: ["me", "threads"] });
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't add to the chat")),
  });
}

/** Take somebody ELSE out of a group chat. Naming yourself is refused by the server — leaving is
 *  {@link useLeaveThread}, a different act with a different audience. */
export function useRemoveThreadMember(threadId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (personId: string) => {
      const { data, error, response } = await api.DELETE("/api/v1/me/threads/{threadId}/members/{personId}", {
        params: { path: { threadId, personId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: threadKey(threadId) });
      void qc.invalidateQueries({ queryKey: ["me", "threads"] });
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't remove from the chat")),
  });
}

/**
 * Take YOURSELF out of a group chat. The thread is REMOVED from the cache rather than invalidated: the
 * caller has just stopped being a participant, so a refetch of it answers 404 — invalidating would replace a
 * chat on screen with an error card, which reads as something having gone wrong rather than as the thing
 * they asked for.
 */
export function useLeaveThread(threadId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await api.POST("/api/v1/me/threads/{threadId}/leave", {
        params: { path: { threadId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => {
      qc.removeQueries({ queryKey: threadKey(threadId) });
      void qc.invalidateQueries({ queryKey: ["me", "threads"] });
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't leave the chat")),
  });
}

/**
 * Mute or unmute the caller's push notifications from one chat (ART-264; moved to the inbox row by ART-270). The
 * chat still lists and badges as before — only the phone stops buzzing. The thread id rides the CALL rather than
 * the hook because the inbox list offers it per row, where a hook per row would mean one mutation each.
 *
 * Optimistic on the list: the row's muted mark is the only feedback the act has, and it should flip as the menu
 * closes rather than a round trip later. Settling refetches both the list and the detail, which carry the flag.
 */
export function useSetThreadMuted() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ threadId, muted }: { threadId: string; muted: boolean }) => {
      const { error, response } = await api.PUT("/api/v1/me/threads/{threadId}/mute", {
        params: { path: { threadId } },
        body: { muted },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onMutate: async ({ threadId, muted }) => {
      await qc.cancelQueries({ queryKey: ["me", "threads"] });
      const previous = qc.getQueriesData<ThreadListResponse>({ queryKey: ["me", "threads"] });
      qc.setQueriesData<ThreadListResponse>({ queryKey: ["me", "threads"] }, (old) =>
        old?.threads
          ? { ...old, threads: old.threads.map((t) => (t.threadId === threadId ? { ...t, muted } : t)) }
          : old,
      );
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      for (const [key, data] of ctx?.previous ?? []) qc.setQueryData(key, data);
      pushToast(errorMessage(err, "Couldn't change notifications for this chat"));
    },
    onSettled: (_data, _err, { threadId }) => {
      void qc.invalidateQueries({ queryKey: ["me", "threads"] });
      void qc.invalidateQueries({ queryKey: threadKey(threadId) });
    },
  });
}

/**
 * Start a conversation: get-or-create the thread, then send the first message — the TWO calls a draft DM
 * costs, exactly once (ART-159).
 *
 * **Why the picker no longer creates anything.** It used to POST on the pick, so choosing a person and then
 * changing your mind left a real, empty thread in both inboxes forever. A draft holds nothing but the
 * person's id until there is something to say, so an abandoned conversation is not swept — it never existed.
 * `core.create_thread` is get-or-create, so a first send also lands correctly in a conversation that already
 * had history.
 */
export function useStartConversation() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ personId, body }: { personId: string; body: string }) => {
      const created = await api.POST("/api/v1/me/threads", { body: { kind: "direct", personId } });
      if (!created.response.ok || !created.data?.threadId) {
        throw new ApiError(created.response.status, created.error);
      }
      const threadId = created.data.threadId as string;
      const sent = await api.POST("/api/v1/me/threads/{threadId}/messages", {
        params: { path: { threadId } },
        body: { body },
      });
      if (!sent.response.ok) throw new ApiError(sent.response.status, sent.error);
      return threadId;
    },
    onSuccess: (threadId) => {
      void qc.invalidateQueries({ queryKey: threadKey(threadId) });
      void qc.invalidateQueries({ queryKey: ["me", "threads"] });
      void qc.invalidateQueries({ queryKey: inboxBadgeKey });
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't send your message")),
  });
}

/**
 * `POST /me/threads/{threadId}/messages` — send a text message. OPTIMISTIC: append a temp bubble
 * immediately, roll back + toast on error, invalidate the thread + lists + badge on settle so the server row
 * (real id, ordering) replaces the temp one. No phantom success — a non-2xx throws.
 */
export function useSendMessage(threadId: string) {
  const { api } = useApp();
  const me = useMe();
  const qc = useQueryClient();
  const key = threadKey(threadId);
  const myPersonId = me.data?.person?.personId;

  return useMutation<MessageDto, Error, string, ThreadSnapshot>({
    mutationFn: async (body) => {
      const { data, error, response } = await api.POST("/api/v1/me/threads/{threadId}/messages", {
        params: { path: { threadId } },
        body: { body },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ThreadDetail>(key);
      const optimistic: MessageDto = {
        messageId: `optimistic-${crypto.randomUUID()}`,
        threadId,
        senderPersonId: myPersonId,
        kind: "text",
        body,
        createdOn: new Date().toISOString(),
        editedOn: null,
        deleted: false,
        reactions: [],
      };
      qc.setQueryData<ThreadDetail>(key, (old) =>
        old ? { ...old, messages: [...(old.messages ?? []), optimistic] } : old,
      );
      return { previous };
    },
    onError: (err, _body, ctx) => {
      rollbackThread(qc, key, ctx?.previous);
      pushToast(errorMessage(err, "Couldn't send your message"));
    },
    onSettled: () => invalidateInbox(qc, key),
  });
}

/** True for an optimistic (not-yet-persisted) message — the temp messageId sentinel. */
export function isPendingMessage(m: MessageDto): boolean {
  return typeof m.messageId === "string" && m.messageId.startsWith("optimistic-");
}

/**
 * `POST /me/threads/{threadId}/messages/{messageId}/reactions` — add the caller's reaction. OPTIMISTIC: patch
 * the message's reaction chips immediately (mine=true), roll back + toast on error, invalidate on settle.
 */
export function useAddReaction(threadId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  const key = threadKey(threadId);

  return useMutation<void, Error, { messageId: string; emoji: string }, ThreadSnapshot>({
    mutationFn: async ({ messageId, emoji }) => {
      const { error, response } = await api.POST(
        "/api/v1/me/threads/{threadId}/messages/{messageId}/reactions",
        { params: { path: { threadId, messageId } }, body: { emoji } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onMutate: async ({ messageId, emoji }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ThreadDetail>(key);
      qc.setQueryData<ThreadDetail>(key, (old) =>
        old
          ? {
              ...old,
              messages: (old.messages ?? []).map((m) =>
                m.messageId === messageId ? { ...m, reactions: addReaction(m.reactions, emoji) } : m,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (err, _v, ctx) => {
      rollbackThread(qc, key, ctx?.previous);
      pushToast(errorMessage(err, "Couldn't add your reaction"));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  });
}

/** `DELETE …/reactions?emoji=` — remove the caller's reaction. OPTIMISTIC (mirrors {@link useAddReaction}). */
export function useRemoveReaction(threadId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  const key = threadKey(threadId);

  return useMutation<void, Error, { messageId: string; emoji: string }, ThreadSnapshot>({
    mutationFn: async ({ messageId, emoji }) => {
      const { error, response } = await api.DELETE(
        "/api/v1/me/threads/{threadId}/messages/{messageId}/reactions",
        { params: { path: { threadId, messageId }, query: { emoji } } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onMutate: async ({ messageId, emoji }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ThreadDetail>(key);
      qc.setQueryData<ThreadDetail>(key, (old) =>
        old
          ? {
              ...old,
              messages: (old.messages ?? []).map((m) =>
                m.messageId === messageId ? { ...m, reactions: removeReaction(m.reactions, emoji) } : m,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (err, _v, ctx) => {
      rollbackThread(qc, key, ctx?.previous);
      pushToast(errorMessage(err, "Couldn't remove your reaction"));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  });
}

/**
 * `DELETE /me/threads/{threadId}/messages/{messageId}` — delete OWN message (204). Server-confirm THEN repaint:
 * the message becomes a "message deleted" tombstone (body null, deleted true) on refetch. Error surfaces verbatim.
 */
export function useDeleteMessage(threadId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  const key = threadKey(threadId);

  return useMutation<void, Error, string>({
    mutationFn: async (messageId) => {
      const { error, response } = await api.DELETE("/api/v1/me/threads/{threadId}/messages/{messageId}", {
        params: { path: { threadId, messageId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => invalidateInbox(qc, key),
    onError: (err) => pushToast(errorMessage(err, "Couldn't delete the message")),
  });
}

/** `POST /me/threads/{threadId}/read` — clear the thread's unread. Server-confirm; repaints the list + badge. */
export function useMarkThreadRead() {
  const { api } = useApp();
  const qc = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (threadId) => {
      const { error, response } = await api.POST("/api/v1/me/threads/{threadId}/read", {
        params: { path: { threadId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["me", "threads"] });
      void qc.invalidateQueries({ queryKey: inboxBadgeKey });
    },
  });
}

/**
 * `POST /me/threads` — get-or-create a thread. Returns the ThreadSummary. A band thread needs an
 * active friendship or the server returns 422 (surfaced as a toast). Invalidates the thread lists so the new
 * row appears. Not optimistic (get-or-create is idempotent and the id is server-assigned).
 */
export function useCreateThread() {
  const { api } = useApp();
  const qc = useQueryClient();

  return useMutation<ThreadSummary, Error, CreateThreadRequest>({
    mutationFn: async (body) => {
      const { data, error, response } = await api.POST("/api/v1/me/threads", { body });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["me", "threads"] }),
    onError: (err) => pushToast(errorMessage(err, "Couldn't start that conversation")),
  });
}

// ---- attachments (ART-159, C-Attach) -------------------------------------------------------------------

/** The ONE file a message may carry, as the thread payload serves it. */
export type AttachmentDto = components["schemas"]["AttachmentDto"];

/**
 * `POST /me/threads/{id}/attachments` — SEND a message carrying one file.
 *
 * One call rather than an upload that returns an id and a send that names it: a staged attachment nobody
 * sends is a row and a blob owned by nobody. `text` is optional — a picture is a message on its own.
 *
 * A RAW fetch, like every other multipart in this app (`useSetAvatar`): the generated client is JSON-bodied.
 *
 * **Deliberately NOT optimistic**, where the text send is. A text bubble can be painted from the string
 * already in the composer; an attachment bubble cannot paint without its bytes, so an optimistic version
 * would either show a broken image or invent a local object URL to swap out a beat later — two renderings
 * of one message, on the path where the send is slowest and most likely to fail. The button reports
 * `isPending` instead, which is the honest version of the same reassurance.
 */
export function useSendAttachment(threadId: string) {
  const { tokenProvider } = useApp();
  const qc = useQueryClient();
  const key = threadKey(threadId);

  return useMutation<MessageDto, Error, { file: File; text?: string }>({
    mutationFn: async ({ file, text }) => {
      const body = new FormData();
      body.append("file", file);
      if (text?.trim()) body.append("text", text.trim());
      const bearer = tokenProvider.getBearer();
      const res = await fetch(`${API_URL}/api/v1/me/threads/${threadId}/attachments`, {
        method: "POST",
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
        credentials: "include",
        body,
      });
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      return (await res.json()) as MessageDto;
    },
    onSuccess: () => invalidateInbox(qc, key),
    onError: (err) => pushToast(errorMessage(err, "Couldn't send that file")),
  });
}

/**
 * An attachment's bytes as a renderable/downloadable URL, or undefined.
 *
 * The `useAvatarUrl` shape, and for the same reason: the serve is AUTHENTICATED and participant-gated
 * server-side, so an `<img src>` cannot reach it — fetch with the bearer and hand back an object URL,
 * revoked on change and unmount. A RAW fetch, never the typed client, because a 404 here is a designed
 * answer (a tombstoned message, an expired chat) and must not trip the global clear-on-401 path.
 *
 * The attachment id is the cache key and attachments are immutable, so this is stable for the life of the
 * message.
 */
export function useAttachmentUrl(
  threadId: string | null | undefined,
  attachmentId: string | null | undefined,
): string | undefined {
  const { tokenProvider } = useApp();
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (!threadId || !attachmentId) {
      setUrl(undefined);
      return;
    }
    let objectUrl: string | null = null;
    const controller = new AbortController();
    void (async () => {
      try {
        const bearer = tokenProvider.getBearer();
        const res = await fetch(`${API_URL}/api/v1/me/threads/${threadId}/attachments/${attachmentId}`, {
          headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
          credentials: "include",
          signal: controller.signal,
        });
        if (!res.ok) return;
        objectUrl = URL.createObjectURL(await res.blob());
        setUrl(objectUrl);
      } catch {
        /* an unfetchable attachment is a designed state — the caller renders a chip without a preview */
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(undefined);
    };
  }, [threadId, attachmentId, tokenProvider]);

  return url;
}

// ---- pinning (ART-159, C-Pin) --------------------------------------------------------------------------

/**
 * `PUT /me/threads/{id}/pin` — pin ONE message to the top, replacing whatever was pinned.
 *
 * ANY participant, which is why this is offered on every message rather than behind the `canEdit` verdict
 * the rename control reads: a name is what the chat IS and a pin is a transient "look at this".
 */
export function usePinMessage(threadId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  const key = threadKey(threadId);

  return useMutation<void, Error, string>({
    mutationFn: async (messageId) => {
      const { error, response } = await api.PUT("/api/v1/me/threads/{threadId}/pin", {
        params: { path: { threadId } },
        body: { messageId },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
    onError: (err) => pushToast(errorMessage(err, "Couldn't pin that message")),
  });
}

/** `DELETE /me/threads/{id}/pin` — clear the pin. Idempotent server-side. */
export function useUnpinMessage(threadId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  const key = threadKey(threadId);

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      const { error, response } = await api.DELETE("/api/v1/me/threads/{threadId}/pin", {
        params: { path: { threadId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
    onError: (err) => pushToast(errorMessage(err, "Couldn't unpin that message")),
  });
}
